import React, { useEffect, useState } from 'react';
import { API_BASE } from '../config';

/**
 * Slide-in user detail panel for admin. Opens on user-row click.
 *
 * Sections:
 *   - Profile (email, name, source, joined, last login, status)
 *   - Account (role, credits, subscription, API key, Stripe customer)
 *   - Recent activity (last 10 AI queries + monthly usage stats)
 *   - Quick actions: grant subscription, grant credits, promote/demote admin,
 *     suspend/unsuspend, copy API key
 */
function UserDetailModal({ userId, token, onClose, onUserChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [grantSubOpen, setGrantSubOpen] = useState(false);
  const [grantTier, setGrantTier] = useState('pro');
  const [grantDuration, setGrantDuration] = useState(1);
  const [grantNote, setGrantNote] = useState('');
  const [busy, setBusy] = useState(false);
  // Delete confirmation state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState('soft'); // 'soft' | 'hard'
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');

  const authedFetch = (path, opts = {}) =>
    fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token },
    }).then(r => r.json());

  const reload = () => {
    setLoading(true);
    authedFetch(`/api/v3/admin/users/${userId}`)
      .then(r => {
        if (!r.success) throw new Error(r.error || 'Failed to load user');
        setDetail(r);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (userId && token) reload(); }, [userId, token]); // eslint-disable-line

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!userId) return null;

  const u = detail?.user;

  const changeRole = async (newRole) => {
    if (!window.confirm(`Set ${u.email}'s role to "${newRole}"?`)) return;
    setBusy(true);
    const r = await authedFetch(`/api/v3/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    setBusy(false);
    if (!r.success) return alert('Failed: ' + (r.error || 'Unknown'));
    onUserChanged?.();
    reload();
  };

  const changeStatus = async (newStatus) => {
    if (!window.confirm(`Set ${u.email} status to "${newStatus}"?`)) return;
    setBusy(true);
    const r = await authedFetch(`/api/v3/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    setBusy(false);
    if (!r.success) return alert('Failed: ' + (r.error || 'Unknown'));
    onUserChanged?.();
    reload();
  };

  const grantCredits = async () => {
    const amountStr = window.prompt(`Grant AI credits to ${u.email}\n(complimentary — no charge)\n\nAmount:`);
    if (!amountStr) return;
    const amount = parseInt(amountStr, 10);
    if (!amount || amount < 1) return alert('Invalid amount');
    const note = window.prompt('Optional note:', '') || '';
    setBusy(true);
    const r = await authedFetch(`/api/v3/admin/users/${userId}/grant-credits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, note }),
    });
    setBusy(false);
    if (!r.success) return alert('Failed: ' + (r.error || 'Unknown'));
    alert(`✓ Granted ${amount} credits. New balance: ${r.new_balance}`);
    onUserChanged?.();
    reload();
  };

  const grantSubscription = async () => {
    const isPerpetual = ['comp', 'enterprise'].includes(grantTier);
    const body = {
      tier: grantTier,
      note: grantNote || undefined,
    };
    if (!isPerpetual) body.duration_months = parseInt(grantDuration, 10);

    setBusy(true);
    const r = await authedFetch(`/api/v3/admin/users/${userId}/grant-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setBusy(false);
    if (!r.success) return alert('Failed: ' + (r.error || 'Unknown'));
    setGrantSubOpen(false);
    setGrantNote('');
    alert(`✓ Granted ${grantTier}${!isPerpetual ? ` for ${body.duration_months} months` : ''}`);
    onUserChanged?.();
    reload();
  };

  const promoteToAdmin = () => changeRole('admin');
  const demoteFromAdmin = () => changeRole('free');

  const performDelete = async () => {
    setBusy(true);
    const url = `/api/v3/admin/users/${userId}${deleteMode === 'hard' ? '?hard=1' : ''}`;
    const r = await authedFetch(url, {
      method: 'DELETE',
      headers: deleteMode === 'hard' ? { 'Content-Type': 'application/json' } : {},
      body: deleteMode === 'hard' ? JSON.stringify({ confirm_email: deleteConfirmEmail }) : undefined,
    });
    setBusy(false);
    if (!r.success) return alert('Failed: ' + (r.error || 'Unknown'));
    alert(r.message);
    onUserChanged?.();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      {/* Panel */}
      <div
        className="relative ml-auto w-full max-w-2xl h-full bg-slate-900 border-l border-slate-700 overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold">👤 User Detail</h2>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none">×</button>
        </div>

        {loading && <div className="p-8 text-white/50">Loading…</div>}
        {error && <div className="p-8 text-red-300">⚠️ {error}</div>}

        {!loading && !error && u && (
          <div className="p-6 space-y-6">
            {/* Profile */}
            <Section title="Profile">
              <Row label="Email">{u.email}</Row>
              <Row label="Name">{u.name || <span className="text-white/40">—</span>}</Row>
              <Row label="Sign-up via">
                {u.google_sub ? <span className="text-blue-300">🔵 Google</span> : <span>📧 Email + password</span>}
              </Row>
              <Row label="Status">
                <span className={`px-2 py-0.5 rounded text-xs uppercase ${
                  u.status === 'active' ? 'bg-green-700' : u.status === 'suspended' ? 'bg-amber-700' : 'bg-red-700'
                }`}>{u.status}</span>
              </Row>
              <Row label="Joined">{new Date(u.created_at).toLocaleString()}</Row>
              <Row label="Last login">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : <span className="text-white/40">never</span>}</Row>
              <Row label="User ID"><code className="text-xs text-white/60">{u.id}</code></Row>
            </Section>

            {/* Account / Role / Subscription */}
            <Section title="Account">
              <Row label="Role">
                <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${roleColors[u.role] || roleColors.free}`}>
                  {u.role}
                </span>
              </Row>
              <Row label="Subscription">
                {u.subscription_status ? (
                  <>
                    <span className="text-green-300">{u.subscription_status}</span>
                    {u.subscription_period_end && (
                      <span className="text-white/50 text-xs ml-2">
                        until {new Date(u.subscription_period_end).toLocaleDateString()}
                      </span>
                    )}
                  </>
                ) : <span className="text-white/40">none</span>}
              </Row>
              <Row label="🪙 Credits balance">{(u.id && detail.usage) ? detail.user.ai_credits_balance || 0 : 0} (top-up)</Row>
              <Row label="AI used this month">
                {detail.usage.ai_queries.monthly} / {detail.limits.ai_per_month === -1 ? '∞' : detail.limits.ai_per_month}
                <span className="text-white/50 ml-2 text-xs">(lifetime: {detail.usage.ai_queries.all_time}, cost ${detail.usage.ai_queries.total_cost_usd?.toFixed(4) || '0.00'})</span>
              </Row>
              <Row label="API calls this month">
                {detail.usage.api_calls.monthly} / {detail.limits.api_per_month === -1 ? '∞' : detail.limits.api_per_month}
                <span className="text-white/50 ml-2 text-xs">(lifetime: {detail.usage.api_calls.all_time})</span>
              </Row>
              <Row label="API Key">
                <div className="flex items-center gap-2 w-full">
                  <code className="flex-1 text-xs bg-slate-800 px-2 py-1 rounded break-all">
                    {showKey ? u.api_key : '••••••••••••' + u.api_key.slice(-8)}
                  </code>
                  <button onClick={() => setShowKey(!showKey)} className="text-xs text-purple-300 hover:text-purple-200">
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                  <button onClick={() => { navigator.clipboard.writeText(u.api_key); alert('Copied'); }} className="text-xs text-purple-300 hover:text-purple-200">
                    Copy
                  </button>
                </div>
              </Row>
              <Row label="Stripe Customer">
                {u.stripe_customer_id ? (
                  <a
                    href={`https://dashboard.stripe.com/customers/${u.stripe_customer_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-purple-300 hover:underline text-xs"
                  >{u.stripe_customer_id} ↗</a>
                ) : <span className="text-white/40">—</span>}
              </Row>
              {u.stripe_subscription_id && (
                <Row label="Subscription ID">
                  <a
                    href={`https://dashboard.stripe.com/subscriptions/${u.stripe_subscription_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-purple-300 hover:underline text-xs"
                  >{u.stripe_subscription_id} ↗</a>
                </Row>
              )}
            </Section>

            {/* Quick Actions */}
            <Section title="Actions">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <ActionButton onClick={() => setGrantSubOpen(true)} color="green" disabled={busy}>
                  💎 Grant Subscription
                </ActionButton>
                <ActionButton onClick={grantCredits} color="amber" disabled={busy}>
                  🪙 Grant Credits
                </ActionButton>
                {u.role === 'admin' ? (
                  <ActionButton onClick={demoteFromAdmin} color="red" disabled={busy}>
                    ⬇️ Demote from Admin
                  </ActionButton>
                ) : (
                  <ActionButton onClick={promoteToAdmin} color="red" disabled={busy}>
                    ⬆️ Promote to Admin
                  </ActionButton>
                )}
                {u.status === 'active' ? (
                  <ActionButton onClick={() => changeStatus('suspended')} color="amber" disabled={busy}>
                    🚫 Suspend
                  </ActionButton>
                ) : (
                  <ActionButton onClick={() => changeStatus('active')} color="green" disabled={busy}>
                    ✓ Unsuspend
                  </ActionButton>
                )}
                <ActionButton onClick={() => { navigator.clipboard.writeText(u.email); alert('Email copied'); }} color="slate" disabled={busy}>
                  📋 Copy Email
                </ActionButton>
                <ActionButton href={`mailto:${u.email}`} color="slate">
                  ✉️ Email User
                </ActionButton>
                <ActionButton onClick={() => { setDeleteOpen(true); setDeleteMode('soft'); setDeleteConfirmEmail(''); }} color="red" disabled={busy}>
                  🗑 Delete Account
                </ActionButton>
              </div>
            </Section>

            {/* Delete confirmation panel */}
            {deleteOpen && (
              <Section title="Delete Account">
                <div className="space-y-3 bg-red-950/30 border border-red-700/50 rounded p-4">
                  <p className="text-xs text-white/70">
                    Choose a delete mode for <strong className="text-white">{u.email}</strong>:
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="del-mode" checked={deleteMode === 'soft'} onChange={() => setDeleteMode('soft')} className="mt-1" />
                      <div className="text-xs">
                        <div className="font-bold text-amber-200">Soft delete (recoverable)</div>
                        <div className="text-white/60">Sets status=deleted. User can't log in, but their data is preserved. Reversible — just change status back to 'active'.</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="del-mode" checked={deleteMode === 'hard'} onChange={() => setDeleteMode('hard')} className="mt-1" />
                      <div className="text-xs">
                        <div className="font-bold text-red-300">Permanent delete (IRREVERSIBLE)</div>
                        <div className="text-white/60">
                          Deletes the user row AND cascades: AI queries, API usage, birth profiles, credit purchases, payments.
                          Stripe customer record is preserved (delete manually in Stripe dashboard if needed).
                        </div>
                      </div>
                    </label>
                  </div>
                  {deleteMode === 'hard' && (
                    <div>
                      <label className="text-xs uppercase text-white/40 mb-1 block">
                        Type <code className="text-amber-200">{u.email}</code> to confirm:
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        placeholder={u.email}
                        value={deleteConfirmEmail}
                        onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                        className="w-full bg-slate-900 border border-red-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                      />
                    </div>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setDeleteOpen(false)} disabled={busy}
                      className="px-3 py-1.5 text-sm text-white/60 hover:text-white">
                      Cancel
                    </button>
                    <button
                      onClick={performDelete}
                      disabled={busy || (deleteMode === 'hard' && deleteConfirmEmail.trim().toLowerCase() !== u.email.toLowerCase())}
                      className={`px-4 py-1.5 rounded text-sm font-bold disabled:opacity-50 ${deleteMode === 'hard' ? 'bg-red-600 hover:bg-red-500' : 'bg-amber-600 hover:bg-amber-500'}`}
                    >
                      {busy ? 'Deleting…' : deleteMode === 'hard' ? 'Permanently Delete' : 'Soft Delete'}
                    </button>
                  </div>
                </div>
              </Section>
            )}

            {/* Grant Subscription form */}
            {grantSubOpen && (
              <Section title="Grant Subscription (offline payment)">
                <div className="space-y-3 bg-slate-800/50 border border-slate-700 rounded p-4">
                  <p className="text-xs text-white/60">
                    Use this when a user paid you directly (cash, bank transfer, etc.) and you want to flip them to a paid tier
                    without Stripe. Recorded in payments as <code>manual_grant</code>.
                  </p>
                  <div>
                    <label className="text-xs uppercase text-white/40 mb-1 block">Tier</label>
                    <select value={grantTier} onChange={e => setGrantTier(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2">
                      <option value="pro">Pro ($19/mo equivalent)</option>
                      <option value="developer">Developer ($49/mo equivalent)</option>
                      <option value="scale">Scale ($199/mo equivalent)</option>
                      <option value="comp">Comp (perpetual, complimentary)</option>
                      <option value="enterprise">Enterprise (perpetual, unlimited)</option>
                      <option value="free">Free (revoke paid plan)</option>
                    </select>
                  </div>
                  {!['comp', 'enterprise', 'free'].includes(grantTier) && (
                    <div>
                      <label className="text-xs uppercase text-white/40 mb-1 block">Duration (months)</label>
                      <input type="number" min="1" max="60" value={grantDuration}
                        onChange={e => setGrantDuration(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2" />
                      <p className="text-xs text-white/40 mt-1">Subscription will expire after this duration.</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs uppercase text-white/40 mb-1 block">Note (audit trail)</label>
                    <input type="text" placeholder="e.g. Paid via bank transfer Apr 2026"
                      value={grantNote} onChange={e => setGrantNote(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setGrantSubOpen(false)} disabled={busy}
                      className="px-3 py-1.5 text-sm text-white/60 hover:text-white">
                      Cancel
                    </button>
                    <button onClick={grantSubscription} disabled={busy}
                      className="px-4 py-1.5 bg-green-600 hover:bg-green-500 rounded text-sm font-bold disabled:opacity-50">
                      {busy ? 'Granting…' : 'Grant'}
                    </button>
                  </div>
                </div>
              </Section>
            )}

            {/* Saved birth profiles — who they're checking for */}
            <Section title={`Saved Birth Profiles (${detail.profile_count || 0})`}>
              {detail.profiles?.length === 0 ? (
                <p className="text-white/40 text-xs">This user hasn't saved any birth profiles yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {detail.profiles.map(p => (
                    <div key={p.id} className="bg-slate-800/50 border border-slate-700 rounded p-2 text-xs flex items-center gap-3">
                      <span className="font-bold text-white">{p.name}</span>
                      {p.relationship && <span className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px] uppercase">{p.relationship}</span>}
                      <span className="text-white/50">· {p.date} {p.time}</span>
                      {p.location_label && <span className="text-white/40">· {p.location_label}</span>}
                      <span className="ml-auto text-white/30">last used {relativeDateFn(p.last_used_at || p.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Recent AI queries */}
            {detail.recent_ai_queries?.length > 0 && (
              <Section title={`Recent AI Activity (last ${detail.recent_ai_queries.length})`}>
                <div className="space-y-2">
                  {detail.recent_ai_queries.map(q => (
                    <div key={q.id} className="bg-slate-800/50 border border-slate-700 rounded p-2 text-xs">
                      <div className="flex justify-between text-white/40 mb-1">
                        <span>{new Date(q.created_at).toLocaleString()}</span>
                        <span>{q.total_tokens || 0} tok · ${(q.cost_usd || 0).toFixed(5)}</span>
                      </div>
                      <p className="text-purple-300">{q.question_preview}</p>
                      {q.error && <p className="text-red-300 mt-1">⚠ {q.error}</p>}
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const roleColors = {
  free: 'bg-slate-700 text-slate-200',
  pro: 'bg-purple-600 text-white',
  developer: 'bg-indigo-600 text-white',
  scale: 'bg-blue-600 text-white',
  enterprise: 'bg-amber-600 text-white',
  comp: 'bg-green-600 text-white',
  admin: 'bg-red-600 text-white',
};

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-bold uppercase tracking-wide text-white/50 mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="text-white/50 min-w-[140px] flex-shrink-0">{label}</span>
      <span className="flex-1">{children}</span>
    </div>
  );
}

function relativeDateFn(ts) {
  if (!ts) return 'never';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.floor(hrs / 24);
  if (days < 7) return days + 'd ago';
  if (days < 30) return Math.floor(days / 7) + 'w ago';
  return new Date(ts).toLocaleDateString();
}

function ActionButton({ onClick, href, children, color = 'slate', disabled }) {
  const palette = {
    green: 'bg-green-600/30 hover:bg-green-600/50 border-green-500/40 text-green-100',
    amber: 'bg-amber-600/30 hover:bg-amber-600/50 border-amber-500/40 text-amber-100',
    red: 'bg-red-600/30 hover:bg-red-600/50 border-red-500/40 text-red-100',
    slate: 'bg-slate-700/40 hover:bg-slate-600/40 border-slate-600/40 text-white/90',
  }[color] || 'bg-slate-700/40';
  const baseClasses = `px-3 py-2 border rounded text-xs font-semibold transition text-center ${palette} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`;
  if (href) return <a href={href} className={baseClasses}>{children}</a>;
  return <button onClick={onClick} disabled={disabled} className={baseClasses}>{children}</button>;
}

export default UserDetailModal;
